export const INTERACT_RANGE = 3.4;
/** Cosine of a wide TPS cone. Camera sits behind Rowan; aim from the player, not the lens. */
export const LOOK_DOT_MIN = 0.15;
/** Inside this reach, facing does not matter — E always takes the nearest live node. */
export const CLOSE_RANGE = 2.1;

export type Xyz = { x: number; y: number; z: number };

export type InteractTarget = {
  id: string;
  prompt: string;
  position: Xyz;
  radius: number;
  live: boolean;
};

export type InteractHit = {
  target: InteractTarget;
  distance: number;
  lookDot: number;
};

export function distanceTo(a: Xyz, b: Xyz): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

/** Player reach after subtracting the target's pickup radius. */
export function reachDistance(player: Xyz, target: InteractTarget): number {
  return Math.max(0, distanceTo(player, target.position) - target.radius);
}

export function lookDotTo(origin: Xyz, look: Xyz, target: Xyz): number {
  const dx = target.x - origin.x;
  const dy = target.y - origin.y;
  const dz = target.z - origin.z;
  const mag = Math.hypot(dx, dy, dz);
  if (mag < 1e-8) {
    return 1;
  }
  const lx = look.x;
  const ly = look.y;
  const lz = look.z;
  const lm = Math.hypot(lx, ly, lz);
  if (lm < 1e-8) {
    return 0;
  }
  return (dx * lx + dy * ly + dz * lz) / (mag * lm);
}

export function lookFromYaw(yaw: number): Xyz {
  return { x: -Math.sin(yaw), y: 0, z: -Math.cos(yaw) };
}

/**
 * Player-forward cone + reach. Closest in-range target wins;
 * higher look-dot breaks ties. Pass the player as `camera` when the boom
 * sits behind Rowan so the TPS lens cannot fail the cone.
 */
export function queryInteract(
  camera: Xyz,
  look: Xyz,
  player: Xyz,
  targets: readonly InteractTarget[],
  range = INTERACT_RANGE,
  minDot = LOOK_DOT_MIN,
): InteractHit | null {
  let best: InteractHit | null = null;
  for (const target of targets) {
    if (!target.live) {
      continue;
    }
    const distance = reachDistance(player, target);
    if (distance > range) {
      continue;
    }
    const dot = lookDotTo(camera, look, target.position);
    if (distance > CLOSE_RANGE && dot < minDot) {
      continue;
    }
    if (!best || distance < best.distance - 1e-6 || (Math.abs(distance - best.distance) <= 1e-6 && dot > best.lookDot)) {
      best = { target, distance, lookDot: dot };
    }
  }
  return best;
}

/** QA / `__DS.interact` — ignore look, pick nearest live target in range. */
export function nearestInteract(
  player: Xyz,
  targets: readonly InteractTarget[],
  range = INTERACT_RANGE,
): InteractHit | null {
  let best: InteractHit | null = null;
  for (const target of targets) {
    if (!target.live) {
      continue;
    }
    const distance = reachDistance(player, target);
    if (distance > range) {
      continue;
    }
    if (!best || distance < best.distance) {
      best = { target, distance, lookDot: 1 };
    }
  }
  return best;
}
